/**
 * Rule-based player-page section selection, mined from the 2026-27 personnel
 * report (design/personnel_report/SPEC.md §3 template inventory + §4
 * per-player table + nba_explorer/projects/personnel_report/specs/*.json).
 *
 * The personnel report's curation reduces to: a universal kit every player
 * gets, plus feature sections chosen by role — and role is legible from the
 * blob itself (action-fingerprint per-100 percentiles, usage, position group,
 * shooting volume, experience). These rules reproduce that curation
 * automatically; PLAYER_OVERRIDES is the per-player hand-tuning hook the
 * analysts had in the one-off (spec files), kept deliberately simple until it
 * graduates to a manual-schema table (design doc open question O5).
 *
 * Smart/algorithmic section weighting is PINNED for later discussion — these
 * stay legible threshold rules on purpose.
 *
 * Template vocabulary (personnel SPEC §3): A* kit, B* on-ball offense,
 * C* screening/interior, D* defense/rebounding, E* shooting.
 */

import type { PlayerStatsScopeRow } from "../../models/PlayerStats";

export type TemplateId =
  // Universal kit (A1-A6): header, box, advanced snapshot, defensive
  // overview, defensive role & efficacy, closeouts.
  | "kit"
  | "pnrBallhandler" // B1 (+ small-small trend folded in, per JM feedback)
  | "isolation" // B3
  | "drives" // B4
  | "transition" // B5 (blob gap — see GAP_NOTES)
  | "pnrScreener" // C1 (pop emphasis auto-detected from roll-type mix)
  | "offBallScreens" // C3
  | "rimFinishing" // C4
  | "crashing" // C5
  | "screenDefense" // D1
  | "rebounding" // D2
  | "positionalSplit" // D3 (blob gap)
  | "shotProfile" // E1 (+ shot chart)
  | "shootingVsDifficulty" // E2
  | "shootingDevelopment" // E3
  | "usageOverTime" // E4
  | "twoWaySummary"; // TW template (league-tagged, low-NBA-sample players)

export interface SectionPlan {
  templates: TemplateId[];
  /** Why each template was chosen — shown nowhere, but keeps the rules
   * debuggable and the overrides reviewable. */
  reasons: Record<string, string>;
}

/** Per-player hand overrides, keyed by NBA player id. `add` wins over rules;
 * `remove` wins over `add`. The storage graduates to a manual-schema table
 * when analysts need to edit without a deploy (O5 precedent:
 * models.manual.Playcall). */
export const PLAYER_OVERRIDES: Record<
  number,
  { add?: TemplateId[]; remove?: TemplateId[] }
> = {};

// Thresholds are percentile-based where the blob publishes a percentile
// (population-honest: "runs a lot of PnR" = p60+ among qualifying players)
// and absolute where the personnel spec used an absolute cut (shooter =
// 3PA/100 of 4.0+ — CONVENTIONS.md's stated threshold).
const P = {
  featureAction: 60, // per-100 pctl to earn an action feature section
  secondaryAction: 75, // pctl for a second on-ball section (B3/B4 alongside B1)
  shooterVol100: 4.0, // 3PA/100 — the personnel "shooting 5" cut
  crashPctl: 70,
  veteranYears: 9, // E4 usage-over-time framing (Jrue precedent)
  lowSamplePoss: 500, // below this NBA poss → two-way/league-tagged framing
};

const num = (row: PlayerStatsScopeRow | undefined, key: string) =>
  (row?.v as Record<string, number> | undefined)?.[key];
const pctl = (row: PlayerStatsScopeRow | undefined, key: string) =>
  (row?.p as Record<string, number> | undefined)?.[key];
/** The highest of the keys' percentiles. A ball-screen role is split PR /
 *  Sm-Sm on NBA blobs (either class earns the feature) and is one all-screens
 *  key on SkillCorner blobs; a scope carries one set. */
const maxPctl = (row: PlayerStatsScopeRow | undefined, keys: string[]) => {
  const ps = keys.map((k) => pctl(row, k)).filter((p): p is number => p !== undefined);
  return ps.length ? Math.max(...ps) : undefined;
};
const PICK_BHR_KEYS = ["f_pr_bhr", "f_smsm_bhr", "f_pick_bhr"];
const PICK_SCR_KEYS = ["f_pr_screener", "f_smsm_screener", "f_pnr_screener"];
const PICK_SCR_DEF_KEYS = ["df_pr_screener", "df_smsm_screener", "df_pnr_screener"];

/**
 * Select the ordered template list for one player's report page.
 *
 * `scope` is the report-season scope row (registry-flattened); `career` the
 * career row (for development/usage-arc sections); `experienceYears` from
 * blob meta; `positionGroup` G/W/F/C from meta.
 */
export function selectSections({
  scope,
  career,
  positionGroup,
  experienceYears,
  playerId,
}: {
  scope?: PlayerStatsScopeRow;
  career?: PlayerStatsScopeRow;
  positionGroup?: string | null;
  experienceYears?: number | null;
  playerId: number;
}): SectionPlan {
  const templates: TemplateId[] = ["kit"];
  const reasons: Record<string, string> = { kit: "universal (A1-A6)" };
  const add = (t: TemplateId, why: string) => {
    if (!templates.includes(t)) {
      templates.push(t);
      reasons[t] = why;
    }
  };

  const poss = scope?.poss ?? 0;
  const big = positionGroup === "C" || positionGroup === "F";

  // Low NBA sample → the two-way treatment (league-tagged summaries), not a
  // confident feature build. Mirrors the personnel TW template + Hansen page.
  if (!scope || poss < P.lowSamplePoss) {
    add("twoWaySummary", `NBA sample ${poss} poss < ${P.lowSamplePoss}`);
    add("shootingDevelopment", "development framing for low-sample players");
    return applyOverrides(playerId, { templates, reasons });
  }

  // --- On-ball offense (B*) — driven by the action fingerprint pctls ---
  const bhr = maxPctl(scope, PICK_BHR_KEYS);
  if (bhr !== undefined && bhr >= P.featureAction)
    add("pnrBallhandler", `PnR BH volume p${bhr}`);
  const iso = pctl(scope, "f_isolation");
  if (iso !== undefined && iso >= P.secondaryAction)
    add("isolation", `iso volume p${iso}`);
  const drv = pctl(scope, "f_drive");
  if (drv !== undefined && drv >= P.secondaryAction)
    add("drives", `drive volume p${drv}`);

  // --- Screening & interior (C*) ---
  const scr = maxPctl(scope, PICK_SCR_KEYS);
  if (scr !== undefined && scr >= P.featureAction)
    add("pnrScreener", `screener volume p${scr}`);
  const obs = pctl(scope, "f_obs");
  if (obs !== undefined && obs >= P.secondaryAction)
    add("offBallScreens", `off-ball screen volume p${obs}`);
  const rim = pctl(scope, "rimPer100");
  if (big || (rim !== undefined && rim >= P.featureAction))
    add("rimFinishing", big ? "big" : `rim volume p${rim}`);
  const crash = pctl(scope, "crash");
  if (crash !== undefined && crash >= P.crashPctl)
    add("crashing", `crash p${crash}`);

  // --- Defense & rebounding (D*) ---
  const scrD = maxPctl(scope, PICK_SCR_DEF_KEYS);
  if (big && scrD !== undefined && scrD >= P.featureAction)
    add("screenDefense", `screen-defense volume p${scrD}`);
  if (big) add("rebounding", "big");

  // --- Shooting (E*) ---
  const vol100 = num(scope, "shVol");
  if (vol100 !== undefined && vol100 >= P.shooterVol100) {
    add("shotProfile", `3PA/100 ${vol100} ≥ ${P.shooterVol100}`);
    add("shootingVsDifficulty", "shooter — diet/lift arc");
  } else if (vol100 !== undefined) {
    add("shootingDevelopment", `3PA/100 ${vol100} — development framing`);
  }
  if ((experienceYears ?? 0) >= P.veteranYears)
    add("usageOverTime", `${experienceYears}y veteran — role-arc section`);

  return applyOverrides(playerId, { templates, reasons });
}

function applyOverrides(playerId: number, plan: SectionPlan): SectionPlan {
  const o = PLAYER_OVERRIDES[playerId];
  if (!o) return plan;
  const templates = [
    ...plan.templates,
    ...(o.add ?? []).filter((t) => !plan.templates.includes(t)),
  ].filter((t) => !(o.remove ?? []).includes(t));
  const reasons = { ...plan.reasons };
  (o.add ?? []).forEach((t) => (reasons[t] = "player override (add)"));
  return { templates, reasons };
}

/**
 * Blob gaps the personnel report filled with one-off SQL — wanted sections
 * whose data is NOT yet in the player blobs. Leaderboard calls decided by the
 * user 2026-08-24:
 *
 * FEEDS LEADERBOARDS → blob for ALL players + player_stats registry columns:
 *  - transition (B5): freq/PPP vs halfcourt + initiator share
 *    (coaching.ctg.transitions.is_primary_initiator — personnel pull #4/04L)
 *  - coverage in the NEW language (B1/D1 tables): blob's baked coverage
 *    breakdown uses the old unified scheme (personnel pulls #2/02L; skill
 *    branches on raw scr_def_type/bhr_def_type). Leaderboards transition to
 *    the new language too, as SEPARATE column families: ballhandler-defender
 *    coverage (Under/Push) and screener-defender coverage (Drop/Up/Switch/
 *    Blitz) — never one folded "coverage" column set.
 *  - rebounding on/off (D2): team ORB/DRB% on vs off (personnel pull #7)
 *  - PnR playmaking (B1/B3 add-ons): AST/100, AST:TOV on action chances
 *    (pull #13)
 *
 * PER-PLAYER ONLY (no leaderboard columns; blob or fetch only where a page
 * needs it):
 *  - positional 4v5 split (D3): ChancePosition shares + per-slot eff (pull #8)
 *  - screener partner matrix (C1): screener × ballhandler pairs (pull #5)
 *  - rim footwork (C4 variant): ctg.interior_shots.takeoff_feet (pull #10;
 *    coverage 2023-24 onward only)
 *  - shot-chart hexbin (E1): blob detail.shots EXISTS but the serving layer
 *    never selects `report:detail` — needs a serving decision, not a blob
 *    extension
 */
export const GAP_NOTES = true;
